import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatNok } from './data';

export type VatMode = 'incl' | 'excl';

const STORAGE_KEY = 'bmg-vat-mode';

interface VatContextValue {
  mode: VatMode;
  setMode: (mode: VatMode) => void;
  formatPrice: (listPrice: number) => string;
}

const VatContext = createContext<VatContextValue | null>(null);

function getStoredMode(): VatMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'excl' ? 'excl' : 'incl';
  } catch {
    return 'incl';
  }
}

export function VatProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<VatMode>('incl');

  useEffect(() => {
    setModeState(getStoredMode());
  }, []);

  const setMode = useCallback((next: VatMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  // List prices are incl. mva for private customers and the same numbers excl. mva for businesses.
  const formatPrice = useCallback((listPrice: number) => formatNok(listPrice), []);

  return <VatContext.Provider value={{ mode, setMode, formatPrice }}>{children}</VatContext.Provider>;
}

export function useVat(): VatContextValue {
  const ctx = useContext(VatContext);
  if (!ctx) throw new Error('useVat must be used within VatProvider');
  return ctx;
}
